import React from 'react';
import * as Sentry from '@sentry/react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: unknown; resetError: () => void }>;
  onError?: (error: unknown, componentStack: string, eventId: string) => void;
}

/**
 * Custom error boundary with i18n support
 * Wraps Sentry.ErrorBoundary with localized fallback UI
 */
export function ErrorBoundary({ children, fallback, onError }: ErrorBoundaryProps) {
  const FallbackComponent = fallback || DefaultFallback;

  return (
    <Sentry.ErrorBoundary fallback={(props) => <FallbackComponent {...props} />} onError={onError}>
      {children}
    </Sentry.ErrorBoundary>
  );
}

/**
 * Default error fallback UI with i18n support
 */
function DefaultFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
  const { _ } = useLingui();

  const errorMessage = error instanceof Error ? error.message : _(msg`An unexpected error occurred`);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: '600px' }}>
        <h1
          style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#1f2937',
          }}
        >
          {_(msg`Something went wrong`)}
        </h1>
        <p style={{ marginBottom: '1.5rem', color: '#6b7280', fontSize: '1.125rem' }}>{errorMessage}</p>
        <button
          onClick={resetError}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
          }}
        >
          {_(msg`Try again`)}
        </button>
      </div>
    </div>
  );
}
